import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

const API_BASE = API_ORIGIN;

// One page for the Access Request tab's View AND Edit actions --
// styled the same as EditUserForm.jsx's "User Details" card (photo-less
// here, since a request has no profile photo yet). View opens this
// read-only (isEditing starts false); Edit opens it already in edit mode
// -- UsersList.jsx tells us which via navigate(..., { state: { mode } }).
//
// Replaces the old inline Accept (check)/Reject (x) icon buttons and the
// Status column entirely: this page always shows a Status dropdown
// (Pending/Accepted/Rejected, defaulting to the request's current
// status), and Save applies whatever status ends up selected -- picking
// "Accepted" reveals the same Role dropdown the old inline accept row
// had (required), picking "Rejected" reveals the same reason field the
// old Reject modal had. See update_access_request() in app.py for
// exactly what each transition does server-side.
export default function EditAccessRequestForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Raw record as loaded from the server -- drives the read-only
  // display. `form` below only gets populated once editing starts.
  const [requestData, setRequestData] = useState(null);

  const [isEditing, setIsEditing] = useState(location.state?.mode === "edit");
  const [form, setForm] = useState({
    Name: "", Email: "", MobileNo: "", Department: "", CompanyID: "",
    Status: "Pending", RoleID: "", RejectionReason: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const loadRequest = () => {
    setLoading(true);
    authFetch(`${API_BASE}/access-requests/${id}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setLoadError(data.error || "Could not load this access request.");
          return;
        }
        setLoadError("");
        setRequestData(data);
      })
      .catch(() => setLoadError("Network error while loading this access request."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    authFetch(`${API_BASE}/roles`).then((res) => res.json()).then((data) => setRoles(Array.isArray(data) ? data : [])).catch(() => setRoles([]));
    authFetch(`${API_BASE}/companies`).then((res) => res.json()).then((data) => setCompanies(Array.isArray(data) ? data : [])).catch(() => setCompanies([]));
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const populateForm = (data) => {
    setForm({
      Name: data.Name || "",
      Email: data.Email || "",
      MobileNo: data.MobileNo || "",
      Department: data.Department || "",
      CompanyID: data.CompanyID || "",
      Status: data.Status || "Pending",
      RoleID: data.RoleID || "",
      RejectionReason: data.RejectionReason || "",
    });
  };

  // If we land already in edit mode (Edit button), the form needs to be
  // populated as soon as the record arrives -- startEditing() below only
  // covers the View -> Edit button click.
  useEffect(() => {
    if (requestData && location.state?.mode === "edit") {
      populateForm(requestData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData]);

  const startEditing = () => {
    if (!requestData) return;
    setSaveError("");
    setSavedMsg("");
    populateForm(requestData);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (submitting) return;
    setIsEditing(false);
    setSaveError("");
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");

    if (!form.Name.trim() || !form.Email.trim()) {
      setSaveError("Please fill in Name and Email.");
      return;
    }
    // Only require a Role when Accepted actually needs to create a new
    // account -- once CreatedUserID already exists, re-picking Accepted
    // just relabels the request (see app.py), no role needed here.
    if (form.Status === "Accepted" && !requestData?.CreatedUserID && !form.RoleID) {
      setSaveError('Please choose a Role before setting Status to "Accepted".');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Name: form.Name.trim(),
          Email: form.Email.trim(),
          MobileNo: form.MobileNo.trim() || null,
          Department: form.Department.trim() || null,
          CompanyID: form.CompanyID || null,
          Status: form.Status,
          RoleID: form.Status === "Accepted" ? form.RoleID || null : undefined,
          RejectionReason: form.Status === "Rejected" ? form.RejectionReason : undefined,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setSaveError(data.error || "Failed to update the access request.");
        return;
      }

      setIsEditing(false);
      setSavedMsg(data.message || "Access request updated successfully.");
      loadRequest();
      // Per the same convention EditUserForm.jsx uses -- saving sends the
      // admin straight back to the Users list (Access Request tab) once
      // the update succeeds.
      navigate(ROUTES.ACCOUNT_USERS);
    } catch (err) {
      setSaveError("Network error while updating the access request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <p className="text-muted mb-0">Loading...</p>
        </div>
      </div>
    );
  }

  if (loadError && !requestData) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="alert alert-danger mb-3">{loadError}</div>
          <Link to={ROUTES.ACCOUNT_USERS} className="btn btn-outline-secondary btn-sm">
            &larr; Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const displayName = requestData?.Name || "-";
  const initials = displayName && displayName !== "-" ? displayName[0].toUpperCase() : "?";
  const displayStatus = isEditing ? form.Status : requestData?.Status;
  // Once a real account exists (CreatedUserID), Email and Status are
  // locked -- the email is that account's login identity, and this
  // request has already been decided, so neither should be changeable
  // from here any more. Name/Mobile/Department/Company/Role stay
  // editable (Role only shows read-only separately, further below).
  const isLocked = !!requestData?.CreatedUserID;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between page-title-head">
        <div className="flex-grow-1">
          <h4 className="m-0 page-main-title">Access Request</h4>
        </div>
        <Link to={ROUTES.ACCOUNT_USERS} className="btn btn-outline-secondary btn-sm">
          &larr; Back to Users
        </Link>
      </div>

      <div className="row">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">Access Request Details</h5>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card-body p-4">
                {loadError && <div className="alert alert-danger">{loadError}</div>}
                {isEditing && saveError && <div className="alert alert-danger">{saveError}</div>}

                <div className="col-12 mb-4 text-center">
                  <div className="d-inline-block position-relative">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
                      style={{
                        height: 110,
                        width: 110,
                        border: "4px solid #0e4d52",
                        background: "#123840",
                        color: "#fff",
                        fontSize: "2.5rem",
                        fontWeight: 700
                      }}
                    >
                      {initials}
                    </span>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Name{isEditing && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing ? (
                      <input
                        className="form-control"
                        value={form.Name}
                        onChange={handleFormChange("Name")}
                        required
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={displayName} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Mob No.</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        className="form-control"
                        value={form.MobileNo}
                        onChange={handleFormChange("MobileNo")}
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={requestData?.MobileNo || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Email ID{isEditing && !isLocked && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing && !isLocked ? (
                      <input
                        type="email"
                        className="form-control"
                        value={form.Email}
                        onChange={handleFormChange("Email")}
                        required
                      />
                    ) : (
                      <input
                        className="form-control custom-field py-2"
                        value={(isEditing ? form.Email : requestData?.Email) || "-"}
                        type="email"
                        readOnly
                      />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Department</label>
                    {isEditing ? (
                      <input
                        className="form-control"
                        value={form.Department}
                        onChange={handleFormChange("Department")}
                        placeholder="e.g. Finance"
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={requestData?.Department || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Company</label>
                    {isEditing ? (
                      <select className="form-select" value={form.CompanyID} onChange={handleFormChange("CompanyID")}>
                        <option value="">-- Select Company --</option>
                        {companies.map((c) => (
                          <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={requestData?.CompanyName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Status{isEditing && !isLocked && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing && !isLocked ? (
                      <select className="form-select" value={form.Status} onChange={handleFormChange("Status")}>
                        <option value="Pending">Pending</option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={displayStatus || "-"} readOnly />
                    )}
                    {isEditing && isLocked && (
                      <div className="form-text mt-1">
                        This request already has an account -- Email and Status can't be changed here any more.
                      </div>
                    )}
                  </div>

                  {/* Role -- only asked for while Accepting a request that
                      doesn't have a real account yet, same as the old
                      inline accept-row dropdown. Once CreatedUserID
                      already exists, the role is fixed and shown
                      read-only instead (change it from Account > Users'
                      own Edit User page). */}
                  {displayStatus === "Accepted" && (
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-green mb-1">
                        Role{isEditing && !requestData?.CreatedUserID && <span className="text-danger"> *</span>}
                      </label>
                      {isEditing && !requestData?.CreatedUserID ? (
                        <select className="form-select" value={form.RoleID} onChange={handleFormChange("RoleID")}>
                          <option value="">-- Select Role --</option>
                          {roles.map((r) => (
                            <option key={r.RoleID} value={r.RoleID}>{r.RoleName}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="form-control custom-field py-2"
                          value={requestData?.RoleName || roles.find((r) => String(r.RoleID) === String(form.RoleID))?.RoleName || "-"}
                          readOnly
                        />
                      )}
                    </div>
                  )}

                  {/* Rejection reason -- only asked for while Rejecting,
                      same as the old Reject modal's text input. */}
                  {displayStatus === "Rejected" && (
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-green mb-1">Rejection Reason</label>
                      {isEditing ? (
                        <input
                          className="form-control"
                          value={form.RejectionReason}
                          onChange={handleFormChange("RejectionReason")}
                          placeholder="Reason for rejecting (optional)"
                        />
                      ) : (
                        <input className="form-control custom-field py-2" value={requestData?.RejectionReason || "-"} readOnly />
                      )}
                    </div>
                  )}
                </div>

                {!isEditing && savedMsg && (
                  <div className="alert alert-success d-flex align-items-center mt-4 mb-0">
                    <i className="ti ti-circle-check me-2 fs-5" />
                    <span>{savedMsg}</span>
                  </div>
                )}

                <div className="text-end mt-4">
                  {isEditing ? (
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-light border" onClick={cancelEditing} disabled={submitting}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
                        {submitting ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="btn btn-primary px-4" onClick={startEditing}>
                      EDIT
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Same custom-field styling as EditUserForm.jsx, scoped to this
          page. */}
      <style>{`
        .custom-field {
          background-color: #f3f3f3 !important;
          border: 1px solid #dcdcdc !important;
          color: #333333 !important;
          border-radius: 2px;
        }
      `}</style>
    </>
  );
}
